import type { Account, Language } from '../types';

export type AccountCategory = 'eval' | 'funded' | 'live' | 'archived';

// Archived (is_active === false) wins over type. Otherwise:
// Live = personal account; Funded = prop account in funded phase; Eval = everything else (demo / challenge)
export function categorizeAccount(acc: Account): AccountCategory {
  if (acc.is_active === false) return 'archived';
  if (acc.account_type === 'personal') return 'live';
  if (acc.prop_phase === 'funded') return 'funded';
  return 'eval';
}

// Display order: Evals → Funded → Live → Archived
export const ACCOUNT_CATEGORY_ORDER: AccountCategory[] = ['eval', 'funded', 'live', 'archived'];

export function accountCategoryLabel(cat: AccountCategory, lang: Language): string {
  if (lang === 'he') {
    return cat === 'eval'
      ? 'תיקי מבחן'
      : cat === 'funded'
        ? 'ממומנים'
        : cat === 'live'
          ? 'Live'
          : 'ארכיון';
  }
  return cat === 'eval'
    ? 'Evals'
    : cat === 'funded'
      ? 'Funded'
      : cat === 'live'
        ? 'Live'
        : 'Archived';
}

// Group accounts by category, preserving input order within each group, dropping empty groups
export function groupAccountsByCategory(
  accounts: Account[],
): { cat: AccountCategory; accounts: Account[] }[] {
  return ACCOUNT_CATEGORY_ORDER.map((cat) => ({
    cat,
    accounts: accounts.filter((a) => categorizeAccount(a) === cat),
  })).filter((g) => g.accounts.length > 0);
}

// ── Broker account rename detection ───────────────────────────────────────────
// Prop firms commonly rename a Tradovate account in place when it changes phase
// (eval → funded) on the SAME broker id. We can't encode each firm's rules, so we
// only detect the name change and let the user decide what it means.

export interface RenamedBrokerAccount {
  tvId: number; // tradovate_account_id (broker id)
  tradelogAccountId: string; // our accounts.id currently linked to it
  oldName: string; // name stored in TradeLog
  newName: string; // current name reported by the broker
  env: 'live' | 'demo'; // broker env of the live account
  timestamp?: string | null; // broker account timestamp (rename/creation), ISO
}

/**
 * Detect connected Tradovate accounts whose broker name no longer matches the
 * name stored in TradeLog. Pure name diff — no firm-specific logic.
 *
 * @param connections  rows linking a broker id to a TradeLog account
 * @param tradelogAccounts  our accounts (for the currently stored name)
 * @param liveAccounts  accounts as currently reported by Tradovate this session
 */
export function detectRenamedTradovateAccounts(
  connections: Array<{ tradovate_account_id: number | null; account_id: string }>,
  tradelogAccounts: Array<{ id: string; name: string }>,
  liveAccounts: Array<{
    id: number;
    name: string;
    env: 'live' | 'demo';
    timestamp?: string | null;
  }>,
): RenamedBrokerAccount[] {
  const tlById = new Map(tradelogAccounts.map((a) => [a.id, a]));
  const liveById = new Map(liveAccounts.map((a) => [a.id, a]));
  const out: RenamedBrokerAccount[] = [];

  for (const conn of connections) {
    if (conn.tradovate_account_id == null) continue;
    const tl = tlById.get(conn.account_id);
    const live = liveById.get(conn.tradovate_account_id);
    if (!tl || !live) continue; // account deleted or not in this OAuth session
    if (tl.name.trim() === live.name.trim()) continue; // unchanged

    out.push({
      tvId: conn.tradovate_account_id,
      tradelogAccountId: tl.id,
      oldName: tl.name,
      newName: live.name,
      env: live.env,
      timestamp: live.timestamp ?? null,
    });
  }
  return out;
}
